import type { ProfileRow } from '../data/db';
import { PROFILE_LIMIT } from '../data/looks';
import type { Animal, Color } from '../data/looks';
import { AnimalIcon, COLOR_LABELS, LABELS } from './animals';
import { Hint, Screen, SecondaryButton, Title } from './kit';

interface Props {
  profiles: ProfileRow[];
  activeId: string;
  onPick: (profileId: string) => void;
  onCreate: () => void;
  onParent: () => void;
  onBack: () => void;
}

/** Экран выбора ребёнка. Детский, а не родительский: здесь только выбор и
 *  заведение. Смена облика и удаление живут в родительском разделе — рядом
 *  с выбором они означали бы, что семилетка одним нажатием стирает занятия
 *  сестры. */
export function ProfilesScreen({
  profiles, activeId, onPick, onCreate, onParent, onBack,
}: Props) {
  /** Активный первым: в обычной семье с двумя детьми прокрутки не будет
   *  вовсе, а при двенадцати профилях свой стоит там, где его ищут. */
  const ordered = [...profiles].sort((a, b) => {
    if (a.id === activeId) return -1;
    if (b.id === activeId) return 1;
    return a.createdAt - b.createdAt;
  });

  return (
    <Screen>
      <Title>Кто занимается?</Title>
      <div className="grid grid-cols-3 gap-3">
        {ordered.map((profile) => (
          <button
            key={profile.id}
            type="button"
            aria-current={profile.id === activeId}
            onClick={() => onPick(profile.id)}
            className={`flex min-h-28 flex-col items-center justify-center gap-1 rounded-2xl border-2 p-2 ${
              profile.id === activeId ? 'border-accent bg-accent-soft' : 'border-line bg-card'
            }`}
          >
            <AnimalIcon animal={profile.animal as Animal} color={profile.color as Color} size={56} />
            <span className="text-[0.8rem] text-muted">
              {COLOR_LABELS[profile.color as Color]} {LABELS[profile.animal as Animal]}
            </span>
          </button>
        ))}
        {profiles.length < PROFILE_LIMIT && (
          <button
            type="button"
            onClick={onCreate}
            className="flex min-h-28 flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-line bg-card p-2 text-[0.9rem] text-muted"
          >
            <span aria-hidden className="text-3xl leading-none">+</span>
            Новый
          </button>
        )}
      </div>
      {profiles.length >= PROFILE_LIMIT && (
        <Hint>Больше {PROFILE_LIMIT} профилей завести нельзя.</Hint>
      )}

      <SecondaryButton bottom onClick={onBack}>
        Назад
      </SecondaryButton>
      <button
        type="button"
        onClick={onParent}
        className="min-h-11 rounded-lg text-[0.95rem] text-muted underline"
      >
        Родителям
      </button>
    </Screen>
  );
}
